'use client';

import React from 'react';
import { Button } from '@/core/presentation/components/ui/Button';
import { formatPlanPriceBrlFromCents, PLAN_BILLING_PERIOD_LABELS, type PlanBillingPeriod } from '@/core/domain';
import { Check, ShieldCheck } from 'lucide-react';
import type { SubscriptionFlowActiveStep } from '@/features/plans/presentation/constants/subscription-flow-steps';
import {
  CHOOSE_PLAN_BENEFITS_CARD_TITLE,
  CHOOSE_PLAN_EQUIVALENT_PREFIX,
  CHOOSE_PLAN_EQUIVALENT_SUFFIX,
  CHOOSE_PLAN_FOOTER_CONFIRM,
  CHOOSE_PLAN_FOOTER_STRIPE,
  CHOOSE_PLAN_FREE_CARD_TITLE_FALLBACK,
  CHOOSE_PLAN_INTERNAL_FREE_ACTIVATING_LABEL,
  CHOOSE_PLAN_INTERNAL_FREE_BILLING_NOTE,
  CHOOSE_PLAN_INTERNAL_FREE_HIGHLIGHT_LABEL,
  CHOOSE_PLAN_MONTHLY_HINT_EMPHASIS,
  CHOOSE_PLAN_MONTHLY_HINT_PREFIX,
  CHOOSE_PLAN_MONTHLY_HINT_SUFFIX,
  CHOOSE_PLAN_PREMIUM_BENEFITS,
  CHOOSE_PLAN_PREMIUM_BADGE_LABEL,
  CHOOSE_PLAN_PREMIUM_PERIOD_UNAVAILABLE,
  CHOOSE_PLAN_SAVINGS_PREFIX,
  CHOOSE_PLAN_SCREEN_EMPTY,
  CHOOSE_PLAN_SCREEN_LOADING_LIST,
  CHOOSE_PLAN_SUBSCRIBE_INTERNAL_FREE_CTA,
  CHOOSE_PLAN_TAB_ARIA_LABEL,
  CHOOSE_PLAN_TAB_MOST_PURCHASED,
} from '@/features/plans/presentation/composables/constants/choose-plan-screen.constants';
import {
  CHOOSE_PLAN_GUARANTEE_BODY,
  CHOOSE_PLAN_GUARANTEE_TITLE,
} from '@/features/plans/presentation/constants/plan-offers-display';
import type { ChoosePlanDualLayoutModel, ChoosePlanPremiumCardDisplay } from '@/features/plans/presentation/utils/choose-plan-dual-layout';
import { CHOOSE_PLAN_SCREEN_BILLING_ORDER } from '@/features/plans/presentation/utils/choose-plan-from-plans';
import { buildFreePlanBenefitLines } from '@/features/plans/presentation/utils/free-plan-display';
import { SubscriptionFlowStepper } from '@/features/plans/presentation/components/SubscriptionFlowStepper';
import { Tabs, TabsList, TabsTrigger } from '@/core/presentation/components/ui/Tabs';
import { cn } from '@/lib/utils';

export type ChoosePlanViewProps = {
  activeStep: SubscriptionFlowActiveStep;
  dualModel: ChoosePlanDualLayoutModel;
  premiumTab: PlanBillingPeriod;
  onPremiumTabChange: (period: PlanBillingPeriod) => void;
  premiumCard: ChoosePlanPremiumCardDisplay | null;
  showMonthlyHint: boolean;
  showListLoading: boolean;
  showEmptyState: boolean;
  onSubscribe: (planId: string) => void;
  checkoutLoadingPlanId: string | null;
  defaultSubscribeCtaLabel: string;
  defaultCheckoutLoadingLabel: string;
};

/** Faixa superior dos cards (badge + abas fantasma) — mesma altura nos dois planos. */
const PLAN_CARD_TOP_ROW_CLASS = 'mb-4 flex min-h-9 items-center justify-between gap-3';

type PlanBenefitsCardProps = {
  lines: readonly string[];
};

/** Reserva altura do rótulo "Mais adquirido" para alinhar título/preço entre os cards. */
function PlanCardMostPurchasedSlot({ visible }: { visible: boolean }) {
  return (
    <p
      className={cn(
        'mt-1 min-h-[1.125rem] text-xs font-medium leading-snug',
        visible ? 'text-cyan-light' : 'invisible'
      )}
      aria-hidden={!visible}
    >
      {CHOOSE_PLAN_TAB_MOST_PURCHASED}
    </p>
  );
}

/** Espaçador invisível com o mesmo tamanho da barra de abas do premium. */
function PremiumPeriodTabsGhost() {
  return (
    <div
      className="pointer-events-none invisible inline-flex h-auto shrink-0 gap-0.5 rounded-lg border border-white/15 bg-white/5 p-0.5"
      aria-hidden
    >
      {CHOOSE_PLAN_SCREEN_BILLING_ORDER.map((period) => (
        <span key={period} className="px-2.5 py-1 text-[11px] font-semibold leading-tight sm:text-xs">
          {PLAN_BILLING_PERIOD_LABELS[period]}
        </span>
      ))}
    </div>
  );
}

function PlanBenefitsCard({ lines }: PlanBenefitsCardProps) {
  return (
    <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-3 text-sm text-white/90">
      <p className="text-xs font-semibold uppercase tracking-wide text-cyan-light/90">
        {CHOOSE_PLAN_BENEFITS_CARD_TITLE}
      </p>
      <ul className="mt-2 space-y-2">
        {lines.map((line) => (
          <li key={line} className="flex items-start gap-2">
            <Check className="h-4 w-4 shrink-0 text-cyan mt-0.5" aria-hidden />
            <span>{line}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export const ChoosePlanView: React.FC<ChoosePlanViewProps> = ({
  activeStep,
  dualModel,
  premiumTab,
  onPremiumTabChange,
  premiumCard,
  showMonthlyHint,
  showListLoading,
  showEmptyState,
  onSubscribe,
  checkoutLoadingPlanId,
  defaultSubscribeCtaLabel,
  defaultCheckoutLoadingLabel,
}) => {
  const checkoutBusy = checkoutLoadingPlanId != null;
  const { freePlan } = dualModel;
  const premiumRef = dualModel.premiumByPeriod[premiumTab];
  const premiumPlanId = premiumRef?.planId ?? premiumCard?.planId ?? null;
  const freeBenefitLines = buildFreePlanBenefitLines(freePlan);

  return (
    <div className="space-y-6">
      <SubscriptionFlowStepper activeStep={activeStep} />

      {showMonthlyHint ? (
        <p className="rounded-lg border border-amber-500/35 bg-amber-500/10 px-4 py-3 text-xs text-amber-50 text-center leading-relaxed">
          {CHOOSE_PLAN_MONTHLY_HINT_PREFIX}
          <strong className="text-white">{CHOOSE_PLAN_MONTHLY_HINT_EMPHASIS}</strong>
          {CHOOSE_PLAN_MONTHLY_HINT_SUFFIX}
        </p>
      ) : null}

      {showListLoading ? (
        <p className="text-center text-sm text-white/70 py-10">{CHOOSE_PLAN_SCREEN_LOADING_LIST}</p>
      ) : null}

      {showEmptyState ? (
        <p className="text-center text-sm text-white/70 py-10 px-2">{CHOOSE_PLAN_SCREEN_EMPTY}</p>
      ) : null}

      {!showListLoading && !showEmptyState ? (
        <>
          <div
            className={cn(
              'grid grid-cols-1 gap-4 items-stretch',
              freePlan ? 'lg:grid-cols-2' : 'lg:max-w-xl lg:mx-auto'
            )}
          >
            {freePlan ? (
              <div className="relative flex h-full flex-col rounded-xl border border-white/15 bg-white/5 p-5 sm:p-6">
                <div className={PLAN_CARD_TOP_ROW_CLASS}>
                  <span className="inline-flex shrink-0 rounded-full bg-white/10 px-2.5 py-1 text-xs font-medium text-white/80">
                    {CHOOSE_PLAN_INTERNAL_FREE_HIGHLIGHT_LABEL}
                  </span>
                  <PremiumPeriodTabsGhost />
                </div>
                <h3 className="text-lg font-semibold text-white">
                  {freePlan.title?.trim() ? freePlan.title : CHOOSE_PLAN_FREE_CARD_TITLE_FALLBACK}
                </h3>
                <PlanCardMostPurchasedSlot visible={false} />
                <p className="mt-3 flex flex-wrap items-baseline gap-1">
                  <span className="text-3xl font-bold text-white">
                    {formatPlanPriceBrlFromCents(0)}
                  </span>
                </p>
                <p className="mt-1 text-xs text-white/60">{CHOOSE_PLAN_INTERNAL_FREE_BILLING_NOTE}</p>

                <PlanBenefitsCard lines={freeBenefitLines} />

                <div className="mt-6 flex flex-1 flex-col justify-end">
                  <Button
                    type="button"
                    size="lg"
                    className="w-full gap-2 font-semibold"
                    disabled={checkoutBusy}
                    loading={checkoutLoadingPlanId === freePlan.id}
                    loadingLabel={CHOOSE_PLAN_INTERNAL_FREE_ACTIVATING_LABEL}
                    onClick={() => void onSubscribe(freePlan.id)}
                  >
                    {CHOOSE_PLAN_SUBSCRIBE_INTERNAL_FREE_CTA}
                  </Button>
                </div>
              </div>
            ) : null}

            <div className="relative flex h-full flex-col rounded-xl border border-white/15 bg-white/5 p-5 sm:p-6">
              <div className={PLAN_CARD_TOP_ROW_CLASS}>
                <span className="inline-flex shrink-0 rounded-full bg-cyan/20 px-2.5 py-1 text-xs font-semibold text-cyan-light">
                  {CHOOSE_PLAN_PREMIUM_BADGE_LABEL}
                </span>
                <Tabs
                  value={premiumTab}
                  onValueChange={(v) => onPremiumTabChange(v as PlanBillingPeriod)}
                  className="ml-auto w-auto shrink-0"
                >
                  <TabsList
                    aria-label={CHOOSE_PLAN_TAB_ARIA_LABEL}
                    className="inline-flex h-auto w-auto gap-0.5 rounded-lg border border-white/15 bg-white/5 p-0.5"
                  >
                    {CHOOSE_PLAN_SCREEN_BILLING_ORDER.map((period) => {
                      const hasPlan = dualModel.premiumByPeriod[period] != null;
                      return (
                        <TabsTrigger
                          key={period}
                          value={period}
                          disabled={!hasPlan}
                          className={cn(
                            'min-h-0 px-2.5 py-1 text-[11px] font-semibold leading-tight sm:text-xs',
                            'text-white/70 data-[state=active]:bg-white data-[state=active]:text-navy-dark',
                            'data-[state=inactive]:hover:bg-white/10 data-[state=active]:shadow-sm',
                            'border border-transparent data-[state=active]:border-border-light',
                            'disabled:opacity-40 disabled:hover:bg-transparent'
                          )}
                        >
                          {PLAN_BILLING_PERIOD_LABELS[period]}
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                </Tabs>
              </div>

              {premiumCard ? (
                <>
                  <h3 className="text-lg font-semibold text-white">{premiumCard.title}</h3>
                  <PlanCardMostPurchasedSlot visible={premiumTab === 'semiannual'} />
                  <p className="mt-3 flex flex-wrap items-baseline gap-1">
                    <span className="text-3xl font-bold text-white">{premiumCard.priceLine}</span>
                    <span className="text-sm text-white/70">{premiumCard.priceSub}</span>
                  </p>
                  <p className="mt-1 text-xs text-white/60">{premiumCard.billingNote}</p>

                  <PlanBenefitsCard lines={CHOOSE_PLAN_PREMIUM_BENEFITS} />

                  {premiumCard.equivalentMonthlyCents != null ? (
                    <div className="mt-3 rounded-lg border border-cyan/25 bg-cyan/10 px-3 py-2.5 text-xs leading-snug text-cyan-light space-y-2">
                      <p className="font-medium text-white">
                        {CHOOSE_PLAN_EQUIVALENT_PREFIX}{' '}
                        <strong>{formatPlanPriceBrlFromCents(premiumCard.equivalentMonthlyCents)}</strong>
                        {CHOOSE_PLAN_EQUIVALENT_SUFFIX}
                      </p>
                      {premiumCard.savingsTotalCents != null && premiumCard.savingsTotalCents > 0 ? (
                        <p className="text-white/90">
                          {CHOOSE_PLAN_SAVINGS_PREFIX}{' '}
                          <strong className="text-cyan-light">
                            {formatPlanPriceBrlFromCents(premiumCard.savingsTotalCents)}
                          </strong>
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </>
              ) : (
                <p className="text-sm text-white/75 leading-relaxed">{CHOOSE_PLAN_PREMIUM_PERIOD_UNAVAILABLE}</p>
              )}

              <div className="mt-6 flex flex-1 flex-col justify-end">
                <Button
                  type="button"
                  size="lg"
                  className="w-full gap-2 font-semibold"
                  disabled={checkoutBusy || !premiumPlanId}
                  loading={premiumPlanId != null && checkoutLoadingPlanId === premiumPlanId}
                  loadingLabel={defaultCheckoutLoadingLabel}
                  onClick={() => {
                    if (premiumPlanId) void onSubscribe(premiumPlanId);
                  }}
                >
                  {defaultSubscribeCtaLabel}
                </Button>
              </div>
            </div>
          </div>

          {premiumCard ? (
            <div
              className="rounded-xl border border-cyan/25 bg-white/[0.06] px-4 py-4 sm:px-5 sm:py-4"
              role="region"
              aria-label={CHOOSE_PLAN_GUARANTEE_TITLE}
            >
              <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cyan/15 text-cyan-light">
                  <ShieldCheck className="h-5 w-5" aria-hidden />
                </div>
                <div className="space-y-1 text-sm text-white/90">
                  <p className="font-semibold text-white">{CHOOSE_PLAN_GUARANTEE_TITLE}</p>
                  <p className="text-xs sm:text-sm text-white/75 leading-relaxed">
                    {CHOOSE_PLAN_GUARANTEE_BODY}
                  </p>
                </div>
              </div>
            </div>
          ) : null}

          <p className="text-center text-xs text-white/65 leading-relaxed px-1">{CHOOSE_PLAN_FOOTER_STRIPE}</p>

          <p className="text-center text-xs text-white/55 px-2">{CHOOSE_PLAN_FOOTER_CONFIRM}</p>
        </>
      ) : null}
    </div>
  );
};
